import { useState } from 'react'
import { findSimilarOptions } from 'utils/utils'
import DidYouMeanDialog from '.'

/** What the user chose for a new name: an existing option, creating it, or nothing (cancelled). */
export type DidYouMeanChoice<T> = { option: T } | { create: true } | null

interface UseDidYouMeanParams<T> {
  /** When false (e.g. admin views), new names are always created without asking. */
  enabled: boolean
  /** The options the picker shows. */
  options: T[]
  /** All names of an option (all languages). */
  getNames: (option: T) => string[]
  /** The option's name in the current language. */
  getLabel: (option: T) => string
  /** Options that can't be picked, e.g. already added. */
  isDisabled: (option: T) => boolean
}

/**
 * "Did you mean" step for pickers that can create new options. `ask(name)` resolves right away to
 * `{ create: true }` when there's no similar option (see findSimilarOptions); otherwise it opens the
 * dialog and resolves to the user's choice. Render `dialog` next to the picker.
 *
 * @returns ask and the dialog element.
 */
export const useDidYouMean = <T extends { id: number }>({
  enabled,
  options,
  getNames,
  getLabel,
  isDisabled,
}: UseDidYouMeanParams<T>) => {
  const [prompt, setPrompt] = useState<{
    name: string
    suggestions: T[]
    resolve: (choice: DidYouMeanChoice<T>) => void
  }>()

  const ask = (name: string): Promise<DidYouMeanChoice<T>> => {
    const suggestions = enabled ? findSimilarOptions(options, name, getNames) : []
    if (!suggestions.length) {
      return Promise.resolve({ create: true })
    }
    return new Promise(resolve => setPrompt({ name, suggestions, resolve }))
  }

  const close = (choice: DidYouMeanChoice<T>) => {
    prompt?.resolve(choice)
    setPrompt(undefined)
  }

  const dialog = (
    <DidYouMeanDialog
      open={!!prompt}
      name={prompt?.name ?? ''}
      suggestions={(prompt?.suggestions ?? []).map(option => ({
        id: option.id,
        label: getLabel(option),
        otherNames: [...new Set(getNames(option))],
        disabled: isDisabled(option),
      }))}
      onPick={id => {
        const option = prompt?.suggestions.find(suggestion => suggestion.id === id)
        close(option ? { option } : null)
      }}
      onCreate={() => close({ create: true })}
      onCancel={() => close(null)}
    />
  )

  return { ask, dialog }
}
